import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Icon from "../../../components/Icon";
import LoadingBlock from "../../../components/LoadingBlock";
import ConversationList, { isOnline } from "./ConversationList";
import MessageList from "./MessageList";
import Composer from "./Composer";
import { initials, fromNow } from "../../../utils/formatters";
import { useAuth } from "../../../contexts/AuthContext";
import { useRefresh } from "../../../contexts/RefreshContext";
import { showApiError } from "../../../utils/toast";
import {
  listConversations, chatContacts, openDirect, getChatMessages, sendChatMessage,
  sendChatMessageWithAttachments, editChatMessage, deleteChatMessage, toggleReaction,
  markChatRead,
} from "../../../api/chatService";
import { isImageFile } from "./attachments";
import "./chat.css";

let tempSeq = 0;

export default function ChatPage() {
  const { user } = useAuth();
  const { refreshKey } = useRefresh();
  // `?c=<id>` deep-links a specific conversation (notification click / refresh).
  const [searchParams, setSearchParams] = useSearchParams();

  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [readMarker, setReadMarker] = useState(null);
  const [unreadBeforeId, setUnreadBeforeId] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [editing, setEditing] = useState(null);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [dragging, setDragging] = useState(false);

  const msgListRef = useRef(null);
  const composerRef = useRef(null);
  const dragDepth = useRef(0);
  const activeIdRef = useRef(null);
  activeIdRef.current = activeId;

  const active = conversations.find((c) => c.id === activeId) || null;

  const refreshConversations = useCallback(async () => {
    try {
      const res = await listConversations();
      const list = res?.data || [];
      setConversations(list);
      return list;
    } catch {
      return [];
    }
  }, []);

  // Initial load: open the conversation named by `?c=<id>` (notification /
  // refresh), falling back to the firm channel when it's absent or stale.
  useEffect(() => {
    (async () => {
      const list = await refreshConversations();
      const wanted = searchParams.get("c");
      let target = wanted ? list.find((c) => String(c.id) === wanted) : null;
      if (!target) target = list.find((c) => c.type === "channel") || list[0];
      if (target) selectConversation(target);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markRead = useCallback(async (convId) => {
    try {
      await markChatRead(convId);
      setConversations((prev) => prev.map((c) => (c.id === convId ? { ...c, unread: 0 } : c)));
    } catch { /* best-effort */ }
  }, []);

  const selectConversation = async (c) => {
    setActiveId(c.id);
    // Persist the open conversation in the URL so a refresh re-opens it.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("c", String(c.id));
        return next;
      },
      { replace: true },
    );
    setMessages([]);
    setHasMore(false);
    setReplyTo(null);
    setEditing(null);
    setUnreadBeforeId(null);
    setLoadingMsgs(true);
    try {
      const res = await getChatMessages(c.id);
      const d = res.data;
      setMessages(d.messages);
      setHasMore(d.has_more);
      setReadMarker(d.read_marker);
      if (c.unread > 0 && d.messages.length) {
        const idx = Math.max(0, d.messages.length - c.unread);
        setUnreadBeforeId(d.messages[idx]?.id ?? null);
      }
    } catch (e) {
      showApiError(e);
    } finally {
      setLoadingMsgs(false);
    }
    markRead(c.id);
  };

  // Merge a freshly-fetched latest page into state without losing older
  // (scrolled-up) history or unresolved optimistic messages.
  const mergeLatest = (incoming) => {
    setMessages((prev) => {
      const map = new Map(prev.map((m) => [m.id, m]));
      incoming.forEach((m) => map.set(m.id, m));
      return Array.from(map.values()).sort((a, b) => {
        const an = typeof a.id === "number", bn = typeof b.id === "number";
        if (an && bn) return a.id - b.id;
        return new Date(a.created_at) - new Date(b.created_at);
      });
    });
  };

  const pollActive = useCallback(async () => {
    const id = activeIdRef.current;
    if (!id) return;
    try {
      const res = await getChatMessages(id);
      mergeLatest(res.data.messages);
      setReadMarker(res.data.read_marker);
      if (msgListRef.current?.isNearBottom()) markRead(id);
    } catch { /* silent */ }
  }, [markRead]);

  // Fast message poll while focused + tab visible (FCM is the primary path).
  useEffect(() => {
    if (!activeId) return undefined;
    const id = setInterval(() => {
      if (!document.hidden && msgListRef.current?.isNearBottom()) pollActive();
    }, 3000);
    return () => clearInterval(id);
  }, [activeId, pollActive]);

  // Slower conversation-list poll (presence + previews + unread).
  useEffect(() => {
    const id = setInterval(refreshConversations, 12000);
    return () => clearInterval(id);
  }, [refreshConversations]);

  // FCM push arrival bumps refreshKey — refresh everything immediately.
  useEffect(() => {
    refreshConversations();
    pollActive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const loadOlder = async () => {
    if (!messages.length || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const res = await getChatMessages(activeId, { before: messages[0].id });
      setMessages((prev) => [...res.data.messages, ...prev]);
      setHasMore(res.data.has_more);
    } catch (e) {
      showApiError(e);
    } finally {
      setLoadingOlder(false);
    }
  };

  // Send one optimistic message: caption + a single group of files (+ reply).
  const sendOne = async (body, files, rid, replySnapshot) => {
    // Optimistic attachments: show local previews (object URLs for images)
    // until the server responds with the real signed URLs.
    const tempAttachments = files.map((file) => {
      const image = isImageFile(file);
      return {
        id: `tmpa-${tempSeq++}`,
        kind: image ? "image" : "file",
        name: file.name,
        size: file.size,
        mime_type: file.type,
        url: image ? URL.createObjectURL(file) : null,
        download_url: null,
        _local: image,
      };
    });
    const temp = {
      id: `tmp-${tempSeq++}`,
      conversation_id: activeId,
      sender: { id: user.id, name: user.name, role: user.role },
      body: body || null, deleted: false, edited: false,
      attachments: tempAttachments,
      reply_to: replySnapshot ? { id: replySnapshot.id, sender_name: replySnapshot.sender?.name, body: replySnapshot.body } : null,
      reactions: [], created_at: new Date().toISOString(), pending: true,
    };
    setMessages((prev) => [...prev, temp]);
    requestAnimationFrame(() => msgListRef.current?.scrollToBottom());
    try {
      const res = files.length
        ? await sendChatMessageWithAttachments(activeId, { body, reply_to_id: rid, files })
        : await sendChatMessage(activeId, body, rid);
      tempAttachments.forEach((a) => a._local && a.url && URL.revokeObjectURL(a.url));
      setMessages((prev) => prev.map((m) => (m.id === temp.id ? res.data : m)));
    } catch (e) {
      setMessages((prev) => prev.map((m) => (m.id === temp.id ? { ...m, pending: false, failed: true } : m)));
      showApiError(e);
    }
  };

  const handleSend = async (body, files = []) => {
    const rid = replyTo?.id || null;
    const reply = replyTo;
    setReplyTo(null);

    // Photos travel together as one album; every non-photo (doc) goes on its
    // own message, WhatsApp-style. The caption + reply ride the first message.
    const images = files.filter(isImageFile);
    const docs = files.filter((f) => !isImageFile(f));
    const groups = [];
    if (images.length) groups.push(images);
    docs.forEach((d) => groups.push([d]));

    if (groups.length === 0) {
      await sendOne(body, [], rid, reply); // text-only
    } else {
      for (let i = 0; i < groups.length; i++) {
        await sendOne(i === 0 ? body : "", groups[i], i === 0 ? rid : null, i === 0 ? reply : null);
      }
    }
    refreshConversations();
  };

  // Whole-conversation drag & drop → stage files in the composer.
  const dragHasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
  const onDragEnter = (e) => {
    if (!active || !dragHasFiles(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };
  const onDragOver = (e) => {
    if (!active || !dragHasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const onDragLeave = () => {
    if (dragDepth.current === 0) return;
    dragDepth.current -= 1;
    if (dragDepth.current === 0) setDragging(false);
  };
  const onDrop = (e) => {
    if (!active) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    const dropped = e.dataTransfer?.files;
    if (dropped?.length) composerRef.current?.addFiles(dropped);
  };

  const handleSaveEdit = async (msg, body) => {
    try {
      const res = await editChatMessage(msg.id, body);
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? res.data : m)));
      setEditing(null);
    } catch (e) {
      showApiError(e);
    }
  };

  const handleDelete = async (msg) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await deleteChatMessage(msg.id);
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, deleted: true, body: null } : m)));
    } catch (e) {
      showApiError(e);
    }
  };

  const handleReact = async (id, emoji) => {
    try {
      const res = await toggleReaction(id, emoji);
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, reactions: res.data.reactions } : m)));
    } catch (e) {
      showApiError(e);
    }
  };

  const jumpToReply = (id) => {
    const el = document.getElementById(`chat-msg-${id}`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className={`chat-shell ${activeId ? "has-active" : ""}`}>
      <ConversationList
        conversations={conversations}
        activeId={activeId}
        currentUserId={user.id}
        onSelect={selectConversation}
        onNewChat={() => setContactsOpen(true)}
      />

      <div
        className="chat-main"
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {dragging && active && (
          <div className="chat-dropzone">
            <div className="chat-dropzone-inner">
              <Icon name="upload" size={40} />
              <p>Drop files to share</p>
              <span>Images, PDF, Word, PowerPoint · up to 4 files · max 2 MB each</span>
            </div>
          </div>
        )}
        {!active ? (
          <div className="chat-empty"><Icon name="message-circle" size={32} /><p>Select a conversation to start chatting.</p></div>
        ) : (
          <>
            <div className="chat-header">
              <button type="button" className="chat-back" onClick={() => setActiveId(null)}><Icon name="arrow-left" size={16} /></button>
              <div className="avatar avatar-sm">{active.type === "channel" ? <Icon name="users" size={14} /> : initials(active.title)}</div>
              <div className="chat-header-info">
                <div className="chat-header-name">{active.title}</div>
                <div className="chat-header-sub">
                  {active.type === "channel"
                    ? `${active.member_count || 0} members`
                    : (isOnline(active.peer?.last_active_at) ? "online" : active.peer?.last_active_at ? `last seen ${fromNow(active.peer.last_active_at)}` : "offline")}
                </div>
              </div>
            </div>

            {loadingMsgs ? (
              <div className="chat-msglist-wrap"><LoadingBlock rows={6} /></div>
            ) : messages.length === 0 ? (
              <div className="chat-empty sm"><p>No messages yet. Say hello 👋</p></div>
            ) : (
              <MessageList
                ref={msgListRef}
                messages={messages}
                currentUserId={user.id}
                isDM={active.type === "direct"}
                readMarker={readMarker}
                hasMore={hasMore}
                loadingOlder={loadingOlder}
                onLoadOlder={loadOlder}
                unreadBeforeId={unreadBeforeId}
                onReply={setReplyTo}
                onReact={handleReact}
                onEdit={setEditing}
                onDelete={handleDelete}
                onJumpToReply={jumpToReply}
              />
            )}

            <Composer
              ref={composerRef}
              replyTo={replyTo}
              onCancelReply={() => setReplyTo(null)}
              editing={editing}
              onCancelEdit={() => setEditing(null)}
              onSend={handleSend}
              onSaveEdit={handleSaveEdit}
            />
          </>
        )}
      </div>

      {contactsOpen && (
        <ContactPicker
          onClose={() => setContactsOpen(false)}
          onPick={async (u) => {
            setContactsOpen(false);
            try {
              const res = await openDirect(u.id);
              const list = await refreshConversations();
              const conv = list.find((c) => c.id === res.data.id) || { ...res.data, unread: 0 };
              selectConversation(conv);
            } catch (e) {
              showApiError(e);
            }
          }}
        />
      )}
    </div>
  );
}

function ContactPicker({ onClose, onPick }) {
  const [q, setQ] = useState("");
  const [people, setPeople] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await chatContacts();
        setPeople(res.data || []);
      } catch {
        setPeople([]);
      }
    })();
  }, []);

  const filtered = (people || []).filter((p) => p.name.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div className="chat-modal-backdrop" onClick={onClose}>
      <div className="chat-modal" onClick={(e) => e.stopPropagation()}>
        <div className="chat-modal-head">
          <span>New chat</span>
          <button type="button" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <input className="input" placeholder="Search people…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
        <div className="chat-contact-list">
          {people === null ? <LoadingBlock rows={4} /> : filtered.length === 0 ? (
            <div className="t-muted" style={{ padding: 12 }}>No people found.</div>
          ) : filtered.map((p) => (
            <button key={p.id} type="button" className="chat-contact" onClick={() => onPick(p)}>
              <div className="avatar avatar-sm">{initials(p.name)}</div>
              <div>
                <div className="chat-contact-name">{p.name}</div>
                <div className="chat-contact-role">{p.role === "admin" ? "Admin" : "Employee"}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
